import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Role } from '@doit/shared';
import { bootstrapSession, http, setAccessToken, setUnauthenticatedHandler } from './client';

export type CurrentUser = {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  status: 'INVITED' | 'ACTIVE' | 'DISABLED';
  phone: string | null;
  initials: string | null;
  lastLoginAt: string | null;
};

type AuthState = {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  setSession: (accessToken: string, user: CurrentUser) => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  // გვერდის გახსნისას სესიის აღდგენა refresh cookie-დან
  useEffect(() => {
    let alive = true;
    void bootstrapSession<CurrentUser>().then((u) => {
      if (alive) {
        setUser(u);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  // სესიის დაკარგვისას მომხმარებელი ავტომატურად „გამოდის"
  useEffect(() => {
    setUnauthenticatedHandler(() => setUser(null));
    return () => setUnauthenticatedHandler(null);
  }, []);

  const setSession = useCallback((accessToken: string, nextUser: CurrentUser) => {
    setAccessToken(accessToken);
    setUser(nextUser);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await http.post<{ accessToken: string; user: CurrentUser }>('/auth/login', {
        email,
        password,
      });
      setSession(res.accessToken, res.user);
      return res.user;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    await http.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, setSession }),
    [user, loading, login, logout, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth უნდა გამოიყენებოდეს <AuthProvider> შიგნით');
  return ctx;
}

/** მიმდინარე მომხმარებელი — გარანტირებულად არსებობს დაცულ მარშრუტებში */
export function useCurrentUser(): CurrentUser {
  const { user } = useAuth();
  if (!user) throw new Error('მომხმარებელი არ არის ავტორიზებული');
  return user;
}
